import { landscapePalette } from "../world/landscapePalette";
/**
 * Ground-vehicle contact FX. Marks and particles are stored in planet-local
 * space, following the same rotating-ground contract as WaterFx, so a planet
 * spin or floating-origin rebase cannot slide them across the terrain.
 */
import * as THREE from "three";
import { createSurfaceColumn, surfaceColumn, seaSurface, type PlanetDef, type TerrainSample } from "@hyperlight/shared";
import type { Planet } from "../world/planet";
import type { WaterFx } from "./waterFx";

const MARK_POOL = 72;
const contact = createSurfaceColumn();
const DUST_POOL = 96;
const SPARK_POOL = 30;
const MARK_LIFE = 1.8;
const DUST_LIFE = 0.75;
const SPARK_LIFE = 0.42;
/** Speed (m/s) where a vehicle starts throwing surface material, and where the
 * plume is at full strength. Both an ABSOLUTE and a RELATIVE test feed the
 * kickup: absolute so a genuinely fast vehicle always kicks up, relative so a
 * slow chassis at the limit still does — a 24 m/s speeder flat out should look
 * like it is working, and 24 m/s on a 68 m/s bike should not. */
const KICK_MIN_SPEED = 24;
const KICK_FULL_SPEED = 62;
/**
 * Metres of ground travel between puffs (floor, and a per-chassis term). Emission
 * is measured in DISTANCE, not time: a timer fires at a fixed rate while the
 * vehicle covers more ground per tick the faster it goes, which is exactly why
 * fast driving read as a dotted line of separate puffs and slow driving did not.
 * Per-metre spacing is constant in world space at any speed, and each puff is
 * placed along the segment actually covered since the last frame rather than all
 * of them landing on the current position.
 *
 * It is also CHEAPER than what it replaces: at full speed this is ~46 spawns/s
 * against the old ~84, because overlapping large soft puffs read as a continuous
 * plume where small hard ones read as pellets.
 */
const SPACING_MIN = 1.2;
const SPACING_PER_RADIUS = 1.1;
/** A drift lays material down roughly twice as densely as a straight line. */
const DRIFT_SPACING_SCALE = 0.45;
/** Ceiling on spawns in one frame, so a hitch cannot dump the whole pool. */
const MAX_SPAWNS_PER_FRAME = 4;
/** Live dust sprites the effect is allowed to sustain. Spacing is derived from
 * this rather than the other way round: at a fixed spacing the live count grows
 * with speed without limit, and a fast drift alone wanted more than the pool
 * holds — at which point particles recycle before they fade and the popping this
 * whole approach removes comes straight back. Well under DUST_POOL so bursts
 * (impacts, a sudden drift) still have somewhere to go. */
const TARGET_LIVE_DUST = 56;
/** Fraction of life spent fading IN. Without it every puff pops on at full
 * opacity, which is what makes them read as discrete events. */
const DUST_FADE_IN = 0.14;

interface Anchored {
  planet: Planet | null;
  local: THREE.Vector3;
  t: number;
}

/**
 * The planet frame to anchor into. This must be the INTERPOLATED render pose,
 * not the sim pose: particles are drawn with `planet.group` (the interpolated
 * transform the terrain renders at), so recording them against `planet.pose`
 * leaves every one of them displaced by a frame of planet spin. On a fast
 * carried world the surface moves >100 m/s, which is metres of offset — the
 * dust drifts consistently to one side of the vehicle instead of sitting under
 * it. main.ts already maintains exactly this frame (updateFxPose) for the
 * ground-anchored boost trails.
 */
export interface FxAnchor {
  center: THREE.Vector3;
  invQuat: THREE.Quaternion;
  /** Forward rotation. Optional only because `planet.pose` is a valid fallback
   * anchor and does not carry one; it is derived from invQuat when absent. */
  quat?: THREE.Quaternion;
}

/** What the chassis is doing this frame, for the contact FX. */
export interface ContactState {
  /** Handbrake drift, 0..1, already scaled by ground contact. */
  drift: number;
  /** Ground speed, m/s — the ABSOLUTE half of the kickup test. */
  speed: number;
  /** speed / maxSpeed — the RELATIVE half, so a slow chassis at its limit
   *  still throws material. */
  speedFraction: number;
  /** Probe contact, 0..1. Zero while gliding, which zeroes the kickup. */
  contact: number;
  /** Collider radius, used to space the emitters at the chassis' rear corners. */
  radius: number;
}

interface Particle extends Anchored {
  sprite: THREE.Sprite;
  velocity: THREE.Vector3;
  life: number;
  size: number;
  water: boolean;
  /** Peak opacity. Dense plumes use a lower one so overlaps do not blow out. */
  peak: number;
}

function smoothstep(edge0: number, edge1: number, value: number): number {
  const t = THREE.MathUtils.clamp((value - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
}

/** CPU approximation of the terrain shader's major color layers for contact FX. */
export function terrainContactColor(
  def: PlanetDef,
  sample: TerrainSample,
  slope: number,
  out: THREE.Color,
  scratch = new THREE.Color(),
): THREE.Color {
  const t = THREE.MathUtils.clamp(sample.temp * 0.5 + 0.5, 0, 1) * 2.999;
  const m = THREE.MathUtils.clamp(sample.moist * 0.5 + 0.5, 0, 1) * 1.999;
  const tr = Math.floor(t);
  const mc = Math.floor(m);
  const tb = t - tr;
  const mb = m - mc;
  const palette = landscapePalette(def);
  const table = palette.table;
  const a = table[tr * 3 + mc];
  const b = table[tr * 3 + mc + 1];
  const c = table[(tr + 1) * 3 + mc];
  const d = table[(tr + 1) * 3 + mc + 1];
  out.setRGB(
    THREE.MathUtils.lerp(THREE.MathUtils.lerp(a[0], b[0], mb), THREE.MathUtils.lerp(c[0], d[0], mb), tb),
    THREE.MathUtils.lerp(THREE.MathUtils.lerp(a[1], b[1], mb), THREE.MathUtils.lerp(c[1], d[1], mb), tb),
    THREE.MathUtils.lerp(THREE.MathUtils.lerp(a[2], b[2], mb), THREE.MathUtils.lerp(c[2], d[2], mb), tb),
  );

  if (def.seaRadius !== null) {
    const seaRel = (def.seaRadius - def.radius) / (def.radius * def.terrain.amplitudeRel);
    const depth = seaRel - sample.h;
    const submerged = smoothstep(0, 0.3, depth);
    const beach = (1 - smoothstep(0.015, 0.07, -depth)) * (1 - submerged);
    scratch.setRGB(...palette.sand);
    out.lerp(scratch.multiplyScalar(THREE.MathUtils.lerp(1, 0.48, submerged)), Math.max(submerged, beach));
  }

  const cliff = 1 - smoothstep(0.55, 0.82, slope);
  out.lerp(scratch.setRGB(...palette.cliff), cliff);
  const cold = 1 - smoothstep(-0.65, -0.35, sample.temp);
  const altitude = smoothstep(def.climate.snowline, def.climate.snowline + 0.15, sample.h);
  const snow = Math.max(cold, altitude) * smoothstep(0.6, 0.8, slope);
  out.lerp(scratch.setRGB(...palette.snow), snow);
  const road = 1 - smoothstep(-0.4, 0.9, sample.road);
  out.lerp(scratch.setRGB(0.56, 0.485, 0.375), road);
  return out;
}

/**
 * How hard the chassis is throwing surface material, 0..1. Pure, exported and
 * tested on its own — the emitter itself needs a canvas and a live planet, so
 * this is the part a headless test can actually pin down.
 *
 * Two speed tests, whichever is greater. ABSOLUTE, so anything genuinely fast
 * kicks up regardless of what it is; and RELATIVE, so a 24 m/s speeder at its
 * limit still looks like it is working while 24 m/s on a 68 m/s bike does not.
 * Both are then scaled by ground contact, because a gliding vehicle has nothing
 * to throw — without that it trails dust off terrain it is flying over.
 */
export function kickAmount(state: ContactState): number {
  // Contact SCALES the kickup but does not have to be perfect: a hover chassis
  // skimming a ridge drops below full probe contact constantly, and gating on
  // that made the plume flicker out exactly when the ride got interesting. Only
  // a genuine glide (no contact at all) silences it.
  return Math.min(1, state.contact * 2.5) * Math.max(
    smoothstep(KICK_MIN_SPEED, KICK_FULL_SPEED, state.speed),
    smoothstep(0.72, 1.0, state.speedFraction) * 0.8,
  );
}

/** Mean particle lifetime at a given kick — the other half of the live budget. */
function dustLife(kick: number): number {
  return DUST_LIFE * (0.75 + 0.45 / 2) * (1 + kick * 0.6);
}

/**
 * Metres of ground between puffs. The aesthetic spacing sets the look at ordinary
 * speeds; above that the BUDGET takes over, widening the spacing so the live
 * count tops out at TARGET_LIVE_DUST however fast the vehicle goes. Puffs get
 * bigger and longer-lived with speed rather than more numerous, which is both
 * the cheaper option and the better-looking one.
 */
export function puffSpacing(radius: number, drifting: boolean, speed = 0, kick = 0): number {
  const look = Math.max(SPACING_MIN, radius * SPACING_PER_RADIUS) * (drifting ? DRIFT_SPACING_SCALE : 1);
  const emitters = drifting ? 2 : 1;
  const budget = (speed * dustLife(kick) * emitters) / TARGET_LIVE_DUST;
  return Math.max(look, budget);
}

/**
 * Live dust sprites a chassis sustains at a given speed. Distance-based emission
 * makes this a straight consequence of spacing and lifetime, and it is the one
 * number that can quietly break the effect: exceed the pool and particles get
 * recycled before they fade, which brings back the popping this replaced. Kept
 * exported and asserted rather than left to be rediscovered.
 */
export function dustLoad(speed: number, radius: number, drifting: boolean, kick = 1): number {
  const perSecond = speed / puffSpacing(radius, drifting, speed, kick);
  return perSecond * dustLife(kick) * (drifting ? 2 : 1); // a drift runs two emitters
}

export const DUST_CAPACITY = DUST_POOL;

function softTexture(inner: string, outer: string): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 64;
  const ctx = canvas.getContext("2d")!;
  const gradient = ctx.createRadialGradient(32, 32, 1, 32, 32, 32);
  gradient.addColorStop(0, inner);
  gradient.addColorStop(0.35, inner);
  gradient.addColorStop(1, outer);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 64, 64);
  return new THREE.CanvasTexture(canvas);
}

function streakTexture(): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = 32;
  canvas.height = 128;
  const ctx = canvas.getContext("2d")!;
  const side = ctx.createLinearGradient(0, 0, 32, 0);
  side.addColorStop(0, "rgba(255,255,255,0)");
  side.addColorStop(0.45, "rgba(255,255,255,0.72)");
  side.addColorStop(0.55, "rgba(255,255,255,0.72)");
  side.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = side;
  ctx.fillRect(0, 0, 32, 128);
  const ends = ctx.createLinearGradient(0, 0, 0, 128);
  ends.addColorStop(0, "rgba(0,0,0,1)");
  ends.addColorStop(0.18, "rgba(0,0,0,0)");
  ends.addColorStop(0.82, "rgba(0,0,0,0)");
  ends.addColorStop(1, "rgba(0,0,0,1)");
  ctx.globalCompositeOperation = "destination-out";
  ctx.fillStyle = ends;
  ctx.fillRect(0, 0, 32, 128);
  return new THREE.CanvasTexture(canvas);
}

export class VehicleFx {
  private marks: (Anchored & { mesh: THREE.Mesh; quaternion: THREE.Quaternion })[] = [];
  private dust: Particle[] = [];
  private sparks: Particle[] = [];
  private nextMark = 0;
  private nextDust = 0;
  private nextSpark = 0;
  /** Ground travel banked toward the next puff, metres. */
  private emitDist = 0;
  private prevLocal = new THREE.Vector3();
  private prevPlanet: Planet | null = null;
  private curLocal = new THREE.Vector3();
  private prevWorld = new THREE.Vector3();
  private trail = new THREE.Vector3();
  private anchorQuat = new THREE.Quaternion();
  private quatScratch = new THREE.Quaternion();
  private right = new THREE.Vector3();
  private back = new THREE.Vector3();
  private dir = new THREE.Vector3();
  private point = new THREE.Vector3();
  private localUp = new THREE.Vector3();
  private localForward = new THREE.Vector3();
  private localRight = new THREE.Vector3();
  private basis = new THREE.Matrix4();
  private world = new THREE.Vector3();
  private surfaceColor = new THREE.Color();
  private colorScratch = new THREE.Color();
  private readonly foamBlue = new THREE.Color(0xdaf5ff);
  private normal = [0, 1, 0];
  /** Frame the current emission records against — see FxAnchor. */
  private anchor: FxAnchor = { center: new THREE.Vector3(), invQuat: new THREE.Quaternion() };
  private terrainSample: TerrainSample = {
    h: 0, temp: 0, moist: 0, river: 0, mountain: 0,
    flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8,
  };

  constructor(private scene: THREE.Scene, private waterFx?: WaterFx) {
    const markGeo = new THREE.PlaneGeometry(1, 1);
    const markTexture = streakTexture();
    for (let i = 0; i < MARK_POOL; i++) {
      const material = new THREE.MeshBasicMaterial({
        map: markTexture,
        transparent: true,
        depthWrite: false,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        side: THREE.DoubleSide,
        opacity: 0,
      });
      const mesh = new THREE.Mesh(markGeo, material);
      mesh.visible = false;
      mesh.renderOrder = 3;
      scene.add(mesh);
      this.marks.push({ mesh, planet: null, local: new THREE.Vector3(), quaternion: new THREE.Quaternion(), t: 0 });
    }

    // White masks let each pooled particle inherit the sampled surface color.
    const dustTexture = softTexture("rgba(255,255,255,0.82)", "rgba(255,255,255,0)");
    const sparkTexture = softTexture("rgba(255,245,190,1)", "rgba(255,103,31,0)");
    for (let i = 0; i < DUST_POOL; i++) this.dust.push(this.makeParticle(dustTexture, THREE.NormalBlending));
    for (let i = 0; i < SPARK_POOL; i++) this.sparks.push(this.makeParticle(sparkTexture, THREE.AdditiveBlending));
  }

  private makeParticle(texture: THREE.Texture, blending: THREE.Blending): Particle {
    const material = new THREE.SpriteMaterial({ map: texture, transparent: true, depthWrite: false, blending, opacity: 0 });
    const sprite = new THREE.Sprite(material);
    sprite.visible = false;
    sprite.renderOrder = 6;
    this.scene.add(sprite);
    return { sprite, planet: null, local: new THREE.Vector3(), velocity: new THREE.Vector3(), t: 0, life: 1, size: 1, water: false, peak: 0.5 };
  }

  /**
   * Contact FX behind a moving chassis: skid marks and thrown material while
   * DRIFTING, and a kicked-up plume whenever the vehicle is simply moving fast.
   * Both tint from the terrain underneath (see terrainContactColor), so a bike
   * throws red dust over canyon rock and pale grit over sand without anything
   * being authored per-biome — and spray instead of dust over water.
   *
   * Marks stay drift-only. Scoring the ground is a thing a sliding vehicle does;
   * a fast straight line should leave the surface alone and only disturb the air.
   */
  emitContact(
    planet: Planet,
    position: THREE.Vector3,
    quaternion: THREE.Quaternion,
    up: THREE.Vector3,
    state: ContactState,
    dt: number,
    anchor?: FxAnchor,
  ): void {
    this.anchor = anchor ?? planet.pose;
    this.anchorQuat.copy(this.anchor.quat ?? this.quatScratch.copy(this.anchor.invQuat).invert());
    const { drift, speed, radius } = state;
    const drifting = drift >= 0.08 && speed >= 6;
    const kick = kickAmount(state);

    // Ground travel since the last frame, measured in PLANET-LOCAL space: on a
    // spinning world a parked vehicle still moves kilometres per second in
    // system space, and distance-based emission would spray dust while stopped.
    this.toLocal(planet, position, this.curLocal);
    const sameGround = this.prevPlanet === planet;
    const moved = sameGround ? this.curLocal.distanceTo(this.prevLocal) : 0;
    // Where the previous emitter position is NOW — the segment to spread this
    // frame's puffs along.
    this.prevWorld.copy(this.prevLocal).applyQuaternion(this.anchorQuat).add(this.anchor.center);
    // A warp or a respawn must not draw a line across the map.
    const continuous = sameGround && moved < 40;
    this.prevPlanet = planet;
    this.prevLocal.copy(this.curLocal);

    if (!drifting && kick <= 0.02) { this.emitDist = 0; return; }

    // Wheel-spin counts as virtual travel, so a vehicle sliding almost in place
    // still throws material rather than going silent.
    this.emitDist += (continuous ? moved : 0) + drift * 5 * dt;
    const spacing = puffSpacing(radius, drifting, speed, kick);
    let count = Math.floor(this.emitDist / spacing);
    if (count <= 0) return;
    if (count > MAX_SPAWNS_PER_FRAME) { count = MAX_SPAWNS_PER_FRAME; this.emitDist = 0; }
    else this.emitDist -= count * spacing;

    this.right.set(1, 0, 0).applyQuaternion(quaternion);
    this.back.set(0, 0, 1).applyQuaternion(quaternion);
    // Emitters sit at the rear corners of whatever chassis this is, rather than
    // at fixed offsets that only ever suited the original speeder.
    const lateral = radius * 0.52;
    // BEHIND the chassis, not under it. `radius` is a collider half-length, so
    // 0.9x put the emitter inside the hull of anything longer than it — the dust
    // spawned within the bodywork and was occluded by it, which is why the
    // straight-line kickup was invisible while the drift (offset to the rear
    // CORNERS) still showed.
    const rear = radius * 1.25;

    for (let i = 0; i < count; i++) {
      // Spread this frame's puffs along the ground actually covered. This is the
      // difference between a continuous plume and a row of blobs at speed.
      const along = continuous ? (i + 0.5) / count : 1;
      this.trail.lerpVectors(this.prevWorld, position, along);
      // Both rear corners either way — a single centre plume sits directly
      // behind the chassis and is hidden by it at exactly the speeds it is
      // supposed to sell. Jittered when not drifting so it does not read as two
      // rails.
      const sides = drifting
        ? [-1, 1]
        : [-0.75 + (Math.random() - 0.5) * 0.3, 0.75 + (Math.random() - 0.5) * 0.3];
      for (const side of sides) {
        this.point.copy(this.trail).addScaledVector(this.right, side * lateral).addScaledVector(this.back, rear);
        const water = this.placeOnSurface(planet, this.point, up);
        if (water) {
          this.waterFx?.foam(planet, this.point, drifting ? 0.45 + drift * 0.55 : 0.2 + kick * 0.45);
        } else if (drifting) {
          this.spawnMark(planet, this.point, this.back, 0.22 + drift * 0.16, Math.min(4.2, 0.9 + speed * 0.035), drift, this.surfaceColor);
        }
        this.spawnDust(planet, this.point, this.right, this.back, up, side, drift, kick, speed, this.surfaceColor, water);
      }
    }
  }

  /** Burst sparks and dust from a hard chassis contact. */
  impact(planet: Planet, position: THREE.Vector3, normal: THREE.Vector3, strength: number, anchor?: FxAnchor): void {
    this.anchor = anchor ?? planet.pose;
    const count = Math.min(14, Math.max(4, Math.round(strength * 0.28)));
    for (let i = 0; i < count; i++) {
      const p = this.sparks[this.nextSpark];
      this.nextSpark = (this.nextSpark + 1) % SPARK_POOL;
      p.planet = planet;
      this.toLocal(planet, position, p.local);
      this.dir.set(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5)
        .applyQuaternion(this.anchor.invQuat)
        .addScaledVector(this.localUp.copy(normal).applyQuaternion(this.anchor.invQuat), 0.9)
        .normalize();
      p.velocity.copy(this.dir).multiplyScalar(3 + Math.random() * Math.min(12, strength * 0.35));
      p.t = p.life = SPARK_LIFE * (0.7 + Math.random() * 0.6);
      p.size = 0.12 + Math.random() * 0.18;
      p.water = false;
      p.sprite.visible = true;
    }
    this.point.copy(position);
    const water = this.placeOnSurface(planet, this.point, normal);
    if (water) this.waterFx?.splash(planet, this.point, Math.min(2.2, 0.8 + strength * 0.04));
    for (let i = 0; i < Math.ceil(count * 0.45); i++) {
      const p = this.dust[this.nextDust];
      this.nextDust = (this.nextDust + 1) % DUST_POOL;
      p.planet = planet;
      this.toLocal(planet, position, p.local);
      p.velocity.copy(normal).applyQuaternion(this.anchor.invQuat).multiplyScalar(1.5 + Math.random() * 2.5);
      p.velocity.x += (Math.random() - 0.5) * 2;
      p.velocity.y += (Math.random() - 0.5) * 2;
      p.velocity.z += (Math.random() - 0.5) * 2;
      p.t = p.life = DUST_LIFE;
      p.size = 0.8 + Math.random() * 1.2;
      p.water = water;
      (p.sprite.material as THREE.SpriteMaterial).color.copy(this.surfaceColor);
      p.sprite.visible = true;
    }
  }

  /** Snap to the rendered contact shell and classify/tint the surface below it. */
  private placeOnSurface(planet: Planet, p: THREE.Vector3, fallbackUp: THREE.Vector3): boolean {
    this.dir.copy(p).sub(this.anchor.center).normalize();
    if (this.dir.lengthSq() < 0.5) this.dir.copy(fallbackUp);
    const terrain = planet.surfaceRadiusWorld(this.dir);
    const radius = planet.def.seaRadius === null ? terrain : Math.max(terrain, planet.def.seaRadius);
    p.copy(this.anchor.center).addScaledVector(this.dir, radius + 0.035);
    this.localUp.copy(this.dir).applyQuaternion(this.anchor.invQuat).normalize();
    if (seaSurface(planet.def) !== 'water'||planet.def.archetype==='volcanic') {
      surfaceColumn(planet.sampler, planet.def, this.localUp.x, this.localUp.y, this.localUp.z, contact);
      p.copy(this.anchor.center).addScaledVector(this.dir, contact.topR + .035);
      if (contact.topMaterial === 'ice') { this.surfaceColor.setRGB(.7, .77, .8); return false; }
      if (contact.topMaterial === 'lava') { this.surfaceColor.setRGB(.08, .055, .04); return false; }
    }
    planet.sampler.sample(this.localUp.x, this.localUp.y, this.localUp.z, this.terrainSample, true);
    const ocean = planet.def.seaRadius !== null && terrain <= planet.def.seaRadius + 0.05;
    const river = this.terrainSample.river > 0.4 && this.terrainSample.h >= planet.sampler.seaRel;
    if (ocean || river) {
      const waterColor = river ? planet.def.palette.water : planet.def.seaColor;
      this.surfaceColor.setRGB(...waterColor).lerp(this.foamBlue, 0.72);
      return true;
    }
    planet.surfaceNormalWorld(this.dir, this.normal, 1.2);
    const slope = THREE.MathUtils.clamp(
      this.dir.x * this.normal[0] + this.dir.y * this.normal[1] + this.dir.z * this.normal[2],
      0,
      1,
    );
    terrainContactColor(planet.def, this.terrainSample, slope, this.surfaceColor, this.colorScratch);
    // Dust catches more light than the ground itself while retaining its hue.
    this.surfaceColor.lerp(this.colorScratch.setRGB(0.82, 0.76, 0.66), 0.14);
    return false;
  }

  private spawnMark(planet: Planet, p: THREE.Vector3, forward: THREE.Vector3, width: number, length: number, opacity: number, color: THREE.Color): void {
    const mark = this.marks[this.nextMark];
    this.nextMark = (this.nextMark + 1) % MARK_POOL;
    mark.planet = planet;
    this.toLocal(planet, p, mark.local);
    this.localUp.copy(mark.local).normalize();
    this.localForward.copy(forward).applyQuaternion(this.anchor.invQuat).addScaledVector(
      this.localUp,
      -this.localForward.dot(this.localUp),
    ).normalize();
    this.localRight.crossVectors(this.localForward, this.localUp).normalize();
    this.basis.makeBasis(this.localRight, this.localForward, this.localUp);
    mark.quaternion.setFromRotationMatrix(this.basis);
    mark.mesh.scale.set(width, length, 1);
    const material = mark.mesh.material as THREE.MeshBasicMaterial;
    material.opacity = 0.42 * opacity;
    material.color.copy(color).multiplyScalar(0.34);
    mark.t = MARK_LIFE;
    mark.mesh.visible = true;
  }

  private spawnDust(
    planet: Planet,
    p: THREE.Vector3,
    right: THREE.Vector3,
    back: THREE.Vector3,
    up: THREE.Vector3,
    side: number,
    drift: number,
    kick: number,
    speed: number,
    color: THREE.Color,
    water: boolean,
  ): void {
    const particle = this.dust[this.nextDust];
    this.nextDust = (this.nextDust + 1) % DUST_POOL;
    particle.planet = planet;
    this.toLocal(planet, p, particle.local);
    // A drift THROWS material sideways; speed alone lifts it and leaves it
    // hanging in the wake, so the two differ in spread rather than in amount.
    const sideways = side * (1.2 + drift * (water ? 3.2 : 2.4) + kick * 0.7);
    particle.velocity.copy(right).multiplyScalar(sideways)
      .addScaledVector(back, (water ? 1.5 : 1) + speed * 0.015 + kick * 1.4)
      .addScaledVector(up, (water ? 1.7 : 0.8) + kick * 1.6 + Math.random() * (water ? 2 : 1.2))
      .applyQuaternion(this.anchor.invQuat);
    particle.t = particle.life = DUST_LIFE * (water ? 0.6 : 1) * (0.75 + Math.random() * 0.45) * (1 + kick * 0.6);
    particle.size = (water ? 0.38 : 0.55) + drift * (water ? 0.55 : 0.8) + kick * 1.0;
    // Denser plumes run at LOWER peak opacity: the read comes from overlap, and
    // at full alpha the overlaps blow out into a solid wall.
    particle.peak = water ? 0.75 : 0.55 - kick * 0.08;
    particle.water = water;
    (particle.sprite.material as THREE.SpriteMaterial).color.copy(color);
    particle.sprite.visible = true;
  }

  update(dt: number): void {
    for (const mark of this.marks) {
      if (mark.t <= 0) continue;
      mark.t -= dt;
      if (mark.t <= 0 || !mark.planet) { mark.mesh.visible = false; continue; }
      mark.mesh.position.copy(mark.local).applyQuaternion(mark.planet.group.quaternion).add(mark.planet.group.position);
      mark.mesh.quaternion.copy(mark.planet.group.quaternion).multiply(mark.quaternion);
      (mark.mesh.material as THREE.MeshBasicMaterial).opacity *= Math.exp(-1.7 * dt);
    }
    this.updateParticles(this.dust, dt, false);
    this.updateParticles(this.sparks, dt, true);
  }

  private updateParticles(pool: Particle[], dt: number, sparks: boolean): void {
    for (const p of pool) {
      if (p.t <= 0) continue;
      p.t -= dt;
      if (p.t <= 0 || !p.planet) { p.sprite.visible = false; continue; }
      const up = this.localUp.copy(p.local).normalize();
      p.velocity.addScaledVector(up, -(sparks ? 12 : p.water ? 8 : 2.2) * dt);
      p.local.addScaledVector(p.velocity, dt);
      this.world.copy(p.local).applyQuaternion(p.planet.group.quaternion).add(p.planet.group.position);
      p.sprite.position.copy(this.world);
      const k = 1 - p.t / p.life;
      const size = p.size * (sparks ? 1 - k * 0.55 : p.water ? 1 + k * 0.45 : 1 + k * 1.5);
      p.sprite.scale.setScalar(Math.max(0.02, size));
      const fadeIn = sparks ? 1 : Math.min(1, k / DUST_FADE_IN);
      (p.sprite.material as THREE.SpriteMaterial).opacity = sparks
        ? (1 - k) * 0.95
        : (1 - k) * fadeIn * p.peak;
    }
  }

  private toLocal(_planet: Planet, p: THREE.Vector3, out: THREE.Vector3): void {
    out.copy(p).sub(this.anchor.center).applyQuaternion(this.anchor.invQuat);
  }

  dispose(): void {
    for (const mark of this.marks) { this.scene.remove(mark.mesh); (mark.mesh.material as THREE.Material).dispose(); }
    for (const p of [...this.dust, ...this.sparks]) { this.scene.remove(p.sprite); (p.sprite.material as THREE.Material).dispose(); }
  }
}
